import React from "react";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { SortableItem } from "./SortableItem";

interface SortableListProps<T> {
  items: T[];
  renderItem: (item: T) => React.ReactNode;
  onReorder: (newOrder: T[]) => void;
  itemKey: keyof T;
  sx?: React.CSSProperties;
  isMovable?: boolean;
}

export function SortableList<T>({ items, renderItem, onReorder, itemKey, sx, isMovable = true }: SortableListProps<T>) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = findItemIndex(items, active.id, itemKey);
    const newIndex = findItemIndex(items, over.id, itemKey);

    if (isValidIndex(oldIndex) && isValidIndex(newIndex)) {
      const newOrder = arrayMove(items, oldIndex, newIndex);
      onReorder(newOrder);
    }
  };

  const findItemIndex = (items: T[], id: string | number, key: keyof T) => {
    return items.findIndex((item) => String(item[key]) === id);
  };

  const isValidIndex = (index: number) => index !== -1;

  return (
    <DndContext
      sensors={isMovable ? sensors : []}
      collisionDetection={closestCenter}
      onDragEnd={isMovable ? handleDragEnd : undefined}
    >
      <SortableContext items={items.map((item) => String(item[itemKey]))} strategy={verticalListSortingStrategy}>
        <div style={sx}>
          {items.map((item: T) => (
            <SortableItem key={String(item[itemKey])} id={String(item[itemKey])} isMovable={isMovable}>
              {renderItem(item)}
            </SortableItem>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
